/**
 * CustomImage is a custom web component for displaying images with optional fallback and skeleton loading.
 * It automatically replaces the initial skeleton with the loaded image or a fallback image if an error occurs.
 */
class CustomImage extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this.handleThemeChange = this.handleThemeChange.bind(this);
  }

  static get observedAttributes() {
    return ["src", "theme-specific"];
  }

  get src() {
    return this.getAttribute("src");
  }

  set src(value) {
    this.setAttribute("src", value);
  }

  get themeSpecific() {
    return this.hasAttribute("theme-specific");
  }

  set themeSpecific(value) {
    if (value) {
      this.setAttribute("theme-specific", "");
    } else {
      this.removeAttribute("theme-specific");
    }
  }

  connectedCallback() {
    if (!this._initialized) {
      this.initialize();
    }
  }

  initialize() {
    this.renderSkeleton();
    this.setupObserver();
    this.setupImage();
    this.updateThemeListener();
    this._initialized = true;
  }

  setupObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === "src" ||
            mutation.attributeName === "theme-specific")
        ) {
          this.updateImageSrc();
        }
      });
    });
    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ["src", "theme-specific"],
    });
  }

  setupImage() {
    const { class: className, alt } = this.attributes;

    if (!this.imageComp) {
      this.imageComp = new Image();
      this.imageComp.addEventListener("error", () => {
        this.imageComp.src =
          "/WebInterface/new-ui/assets/images/no-image-available.png";
      });

      this.imageComp.addEventListener("load", () => {
        this.imageComp.classList.remove("is-invisible");
        this.querySelector(".skeleton")?.remove();
      });

      this.appendChild(this.imageComp);
    }

    if (className?.value) {
      this.imageComp.className = className.value;
    }
    this.imageComp.classList.add("is-invisible");
    this.imageComp.alt = alt?.value || "";

    this.updateImageSrc();
  }

  updateImageSrc() {
    if (!this.imageComp) {
      this.setupImage();
      return;
    }

    const currentSrc = this.getAttribute("src");
    if (!currentSrc) return;

    if (this.themeSpecific) {
      const currentTheme = localStorage.getItem("data-theme") || "light";
      const currentLanguage = localStorage.getItem("selected-language") || "en";
      const lastDotIndex = currentSrc.lastIndexOf(".");
      if (lastDotIndex !== -1) {
        const generateSrc = (suffix) =>
          currentSrc.substring(0, lastDotIndex) +
          suffix +
          currentSrc.substring(lastDotIndex);

        if (currentLanguage) {
          if (currentTheme === "light") {
            const lightLanguageSrc = generateSrc(`_${currentLanguage}`);
            this.checkImageExists(lightLanguageSrc, (exists) => {
              this.imageComp.src = exists ? lightLanguageSrc : currentSrc;
            });
          } else if (currentTheme === "dark") {
            const darkLanguageSrc = generateSrc(`_${currentLanguage}_dark`);
            this.checkImageExists(darkLanguageSrc, (exists) => {
              if (exists) {
                this.imageComp.src = darkLanguageSrc;
              } else {
                const darkSrc = generateSrc("_dark");
                this.checkImageExists(darkSrc, (exists) => {
                  if (exists) {
                    this.imageComp.src = darkSrc;
                  } else {
                    const lightSrc = generateSrc(`_${currentLanguage}`);
                    this.checkImageExists(lightSrc, (exists) => {
                      this.imageComp.src = exists ? lightSrc : currentSrc;
                    });
                  }
                });
              }
            });
          }
          return;
        } else if (currentTheme === "dark") {
          const darkSrc = generateSrc("_dark");
          this.checkImageExists(darkSrc, (exists) => {
            this.imageComp.src = exists ? darkSrc : currentSrc;
          });
          return;
        }
      }
    }

    this.imageComp.src = currentSrc;
  }

  checkImageExists(url, callback) {
    const img = new Image();
    img.onload = () => callback(true);
    img.onerror = () => callback(false);
    img.src = url;
  }

  handleThemeChange() {
    this.updateImageSrc();
  }

  updateThemeListener() {
    if (this.themeSpecific) {
      window.addEventListener("themeChanged", this.handleThemeChange);
      window.addEventListener("languageChanged", this.handleThemeChange);
    } else {
      window.removeEventListener("themeChanged", this.handleThemeChange);
      window.removeEventListener("languageChanged", this.handleThemeChange);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this._initialized) {
      this.initialize();
    }
    if (name === "src" && oldValue !== newValue) {
      this.updateImageSrc();
    } else if (name === "theme-specific") {
      this.updateThemeListener();
      this.updateImageSrc();
    }
  }

  disconnectedCallback() {
    window.removeEventListener("themeChanged", this.handleThemeChange);
    window.removeEventListener("languageChanged", this.handleThemeChange);
    this.observer?.disconnect();
  }

  /**
   * Renders the skeleton loading placeholder for the image.
   */
  renderSkeleton() {
    this.innerHTML = `
      <div class="skeleton skeleton-rect" style="--rect-h: 100%; --c-w: 100%; --c-p: 0%; --lines: 0; --bg: var(--bg-skeleton);"></div>
    `;
  }
}

// Define the custom element "w-image" for the CustomImage class
window.customElements.define("w-image", CustomImage);
